// src/store/features/message/updateFlowStates.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { selectInputValue } from "../ui/uiSelectors";
import {
  setIsTextFlow,
  setIsVoiceFlow,
  setIsSocketDataReadyToSend,
} from "./textContentSlice";

/**
 * updateFlowStates
 * 🔄 Dynamically determines the current flow mode (Text / Voice)
 * based on the UI input and textContent state values.
 */
export const updateFlowStates = createAsyncThunk(
  "textContent/updateFlowStates",
  async (_, { getState, dispatch }) => {
    const state = getState();

    // 1️⃣ Get user input (text) from UI slice
    const inputValue = selectInputValue(state);
    const hasUserText = !!inputValue?.trim();

    // 2️⃣ Extract textContent-related data
    const textState = state.textContent;
    const {
      recordedBase64Data,
      accumulatedFinalTranscript,
      currentInterimTranscript,
      isSocketRecording,
      selectedChatId,
      voiceFlowLocks,
      textFlowLocks,
    } = textState;

    // ✅ Derived conditions
    const hasAudio = !!recordedBase64Data?.trim();
    const hasTranscript =
      !!accumulatedFinalTranscript?.trim() ||
      !!currentInterimTranscript?.trim();

    const isVoiceLocked = selectedChatId
      ? voiceFlowLocks[selectedChatId]
      : false;
    const isTextLocked = selectedChatId ? textFlowLocks[selectedChatId] : false;

    //console.log(`Flow states are as below 👇`, `hasAudio : ${hasAudio} `, `hasTranscript : ${hasTranscript} `, `hasUserText : ${hasUserText} `, `isVoiceLocked : ${isVoiceLocked} `, `isTextLocked : ${isTextLocked} `, `isSocketRecording : ${isSocketRecording} `);


    // 3️⃣ Strict Logic for determining flow mode

    // 🎤 PRIORITY: Voice Flow (audio + transcript present)
    if (hasAudio && hasTranscript) {
      dispatch(setIsVoiceFlow(true));
      dispatch(setIsSocketDataReadyToSend(!isSocketRecording));
      dispatch(setIsTextFlow(false));
      return;
    }

    // ✍️ Text Flow (no audio, but user entered text)
    if (!hasAudio && hasUserText) {
      dispatch(setIsTextFlow(true));
      dispatch(setIsVoiceFlow(false));
      dispatch(setIsSocketDataReadyToSend(false));
      return;
    }

    // 💤 Default case: nothing active
    dispatch(setIsTextFlow(false));
    dispatch(setIsVoiceFlow(false));
    dispatch(setIsSocketDataReadyToSend(false));

    // 🔒 Enforce Locked States
    if (isVoiceLocked && hasAudio && hasTranscript) {
      dispatch(setIsVoiceFlow(true));
      dispatch(setIsSocketDataReadyToSend(true));
    }

    if (isTextLocked && hasUserText) {
      dispatch(setIsTextFlow(true));
    }    
  }
);
